import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Switch } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { constants } from '../constants';
const ApplicationModal = ({ open, onClose, onSave, application, loading = false, error = null }) => {
    const [formData, setFormData] = useState({
        name: '',
        type: 'single',
        description: '',
        productName: '',
        url: '',
        applicationDescription: '',
        applicationState: true,
        image: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const isEditMode = !!application;
    // Reset form when modal opens/closes or application changes
    useEffect(() => {
        if (open) {
            if (application) {
                setFormData({
                    name: application.name,
                    type: application.type || 'single',
                    description: application.description || '',
                    productName: application.details?.productName || '',
                    url: application.details?.productUrl || '',
                    applicationDescription: application.details?.applicationDescription || '',
                    applicationState: application.details?.applicationState !== 'inactive',
                    image: null
                });
                if (application.details?.image_base64) {
                    setImagePreview(application.details.image_base64);
                }
                else {
                    setImagePreview(null);
                }
            }
            else {
                setFormData({
                    name: '',
                    type: 'single',
                    description: '',
                    productName: '',
                    url: '',
                    applicationDescription: '',
                    applicationState: true,
                    image: null
                });
                setImagePreview(null);
            }
        }
    }, [open, application]);
    const handleSave = () => {
        onSave(formData, imagePreview);
    };
    const handleClose = () => {
        setImagePreview(null);
        onClose();
    };
    const getSubmitButtonText = () => {
        if (loading) {
            return _jsx(CircularProgress, { size: 24 });
        }
        return isEditMode
            ? constants.configuration_modal_button_update
            : constants.configuration_modal_button_add;
    };
    return (_jsxs(Dialog, { open: open, onClose: handleClose, maxWidth: "sm", fullWidth: true, children: [_jsx(DialogTitle, { sx: { fontSize: '20px', fontWeight: 600 }, children: isEditMode
                    ? constants.application_modal_title_edit
                    : constants.application_modal_title_add }), _jsxs(DialogContent, { sx: { pt: 2 }, children: [error && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, children: error })), _jsx(TextField, { fullWidth: true, label: constants.application_modal_field_name, value: formData.name, onChange: (e) => setFormData({
                            ...formData,
                            name: e.target.value
                        }), required: true, margin: "normal", disabled: loading, placeholder: constants.application_modal_placeholder_name, slotProps: { htmlInput: { maxLength: 100 } } }), _jsx(TextField, { fullWidth: true, label: constants.application_modal_field_description, value: formData.description, onChange: (e) => setFormData({
                            ...formData,
                            description: e.target.value
                        }), margin: "normal", disabled: loading, placeholder: constants.application_modal_placeholder_description, slotProps: { htmlInput: { maxLength: 150 } } }), _jsxs(FormControl, { fullWidth: true, margin: "normal", required: true, children: [_jsx(InputLabel, { children: constants.application_modal_field_type }), _jsxs(Select, { value: formData.type, onChange: (e) => setFormData({
                                    ...formData,
                                    type: e.target.value
                                }), label: constants.application_modal_field_type, disabled: loading, children: [_jsx(MenuItem, { value: "single", children: constants.application_modal_type_single }), _jsx(MenuItem, { value: "group", children: constants.application_modal_type_group })] })] }), _jsx(TextField, { fullWidth: true, label: constants.application_modal_field_product_name, value: formData.productName, onChange: (e) => setFormData({
                            ...formData,
                            productName: e.target.value
                        }), required: true, margin: "normal", disabled: loading, placeholder: constants.application_modal_placeholder_product_name, slotProps: { htmlInput: { maxLength: 100 } } }), _jsx(TextField, { fullWidth: true, label: constants.application_modal_field_url, value: formData.url, onChange: (e) => setFormData({
                            ...formData,
                            url: e.target.value
                        }), required: true, margin: "normal", disabled: loading, placeholder: constants.application_modal_placeholder_url, slotProps: { htmlInput: { maxLength: 500 } } }), _jsx(Box, { sx: { mt: 2, mb: 0.5 }, children: _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5
                            }, children: [_jsx(Typography, { variant: "caption", sx: {
                                        color: '#595959',
                                        fontSize: '12px',
                                        lineHeight: '1.28',
                                        fontWeight: 500
                                    }, children: constants.application_modal_field_app_description }), _jsx("textarea", { value: formData.applicationDescription, onChange: (e) => {
                                        const text = e.target.value.substring(0, 500);
                                        setFormData({
                                            ...formData,
                                            applicationDescription: text
                                        });
                                    }, placeholder: constants.application_modal_placeholder_app_description, disabled: loading, maxLength: 500, rows: 4, style: {
                                        width: '100%',
                                        padding: '12px 12px',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '4px',
                                        fontFamily: 'Open Sans, sans-serif',
                                        fontSize: '16px',
                                        color: '#212121',
                                        boxSizing: 'border-box',
                                        resize: 'vertical',
                                        fontWeight: 400,
                                        lineHeight: '1.52',
                                        transition: 'all 0.3s ease',
                                        backgroundColor: '#ffffff',
                                        outline: 'none'
                                    }, onFocus: (e) => {
                                        e.currentTarget.style.borderColor = '#26890d';
                                        e.currentTarget.style.boxShadow =
                                            '0 0 0 2px rgba(38, 137, 13, 0.2)';
                                        e.currentTarget.style.outline = 'none';
                                    }, onBlur: (e) => {
                                        e.currentTarget.style.borderColor = '#d9d9d9';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }, onMouseEnter: (e) => {
                                        if (document.activeElement !== e.currentTarget) {
                                            e.currentTarget.style.borderColor =
                                                '#bdbdbd';
                                        }
                                    }, onMouseLeave: (e) => {
                                        if (document.activeElement !== e.currentTarget) {
                                            e.currentTarget.style.borderColor =
                                                '#d9d9d9';
                                        }
                                    } })] }) }), _jsxs(Box, { sx: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 0.5,
                            mb: 2,
                            fontSize: '12px',
                            color: '#595959'
                        }, children: [_jsx(Typography, { variant: "caption", children: constants.application_modal_max_characters }), _jsxs(Typography, { variant: "caption", children: [formData.applicationDescription.length, "/500"] })] }), _jsxs(Box, { sx: { mt: 2, mb: 2 }, children: [_jsx(Typography, { variant: "caption", sx: {
                                    color: '#595959',
                                    fontSize: '12px',
                                    lineHeight: '1.28',
                                    fontWeight: 500,
                                    mb: 1,
                                    display: 'block'
                                }, children: constants.application_modal_field_app_state }), _jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }, children: [_jsx(Typography, { sx: {
                                            fontSize: '14px',
                                            color: formData.applicationState
                                                ? '#949494'
                                                : '#212121',
                                            fontWeight: formData.applicationState
                                                ? 400
                                                : 600
                                        }, children: constants.application_modal_state_inactive }), _jsx(Switch, { checked: formData.applicationState, onChange: (e) => setFormData((prev) => ({
                                            ...prev,
                                            applicationState: e.target.checked
                                        })), disabled: loading, sx: {
                                            '& .MuiSwitch-switchBase': {
                                                color: '#bdbdbd',
                                                '&.Mui-checked': {
                                                    color: '#26890d',
                                                    '& + .MuiSwitch-track': {
                                                        backgroundColor: '#26890d'
                                                    }
                                                }
                                            }
                                        } }), _jsx(Typography, { sx: {
                                            fontSize: '14px',
                                            color: formData.applicationState
                                                ? '#26890d'
                                                : '#949494',
                                            fontWeight: formData.applicationState
                                                ? 600
                                                : 400
                                        }, children: constants.application_modal_state_active })] })] }), _jsxs(Box, { sx: { mt: 2, mb: 2 }, children: [_jsx("input", { accept: "image/*", style: { display: 'none' }, id: "image-upload", type: "file", onChange: (e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                        setFormData({
                                            ...formData,
                                            image: file
                                        });
                                        // Create preview
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                            setImagePreview(reader.result);
                                        };
                                        reader.readAsDataURL(file);
                                    }
                                } }), imagePreview ? (_jsxs(Box, { sx: {
                                    border: '1px solid #ebebeb',
                                    borderRadius: '16px',
                                    p: 2,
                                    backgroundColor: '#ffffff',
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center'
                                }, children: [_jsxs(Box, { sx: {
                                            width: '60px',
                                            height: '60px',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                            backgroundColor: '#f5f5f5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }, children: [_jsx("img", { src: imagePreview, alt: "Preview", style: {
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                } }), _jsx(Box, { sx: {
                                                    position: 'absolute',
                                                    inset: 0,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.5)'
                                                } })] }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { variant: "subtitle2", sx: {
                                                    fontWeight: 600,
                                                    color: '#212121',
                                                    fontSize: '16px',
                                                    lineHeight: '1.52'
                                                }, children: formData.image?.name ||
                                                    constants.application_modal_image_title_default }), _jsx(Typography, { variant: "caption", sx: {
                                                    color: '#595959',
                                                    fontSize: '12px',
                                                    lineHeight: '1.28'
                                                }, children: formData.image
                                                    ? `${(formData.image.size / 1024 / 1024).toFixed(2)}MB/10MB`
                                                    : '0MB/10MB' })] }), _jsx(Box, { component: "label", htmlFor: "image-upload", sx: {
                                            display: 'flex',
                                            gap: 0.5,
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            p: 1,
                                            borderRadius: '4px',
                                            transition: 'background-color 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                            }
                                        }, children: _jsx(DeleteIcon, { sx: {
                                                fontSize: '24px',
                                                color: '#212121',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    color: '#da291c'
                                                }
                                            }, onClick: (e) => {
                                                e.stopPropagation();
                                                setFormData({
                                                    ...formData,
                                                    image: null
                                                });
                                                setImagePreview(null);
                                            } }) })] })) : (_jsx("label", { htmlFor: "image-upload", children: _jsx(Box, { component: "div", sx: {
                                        border: '1px solid #ebebeb',
                                        borderRadius: '16px',
                                        p: 2,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        backgroundColor: '#ffffff',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5',
                                            borderColor: '#d9d9d9'
                                        }
                                    }, children: _jsxs(Box, { sx: {
                                            display: 'flex',
                                            gap: 2,
                                            alignItems: 'center',
                                            justifyContent: 'flex-start'
                                        }, children: [_jsx(Box, { sx: {
                                                    width: '60px',
                                                    height: '60px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#f5f5f5',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }, children: _jsx(Typography, { sx: {
                                                        fontSize: '24px',
                                                        color: '#949494'
                                                    }, children: "\uD83D\uDCC1" }) }), _jsxs(Box, { sx: { flex: 1, textAlign: 'left' }, children: [_jsx(Typography, { variant: "body2", sx: {
                                                            color: '#212121',
                                                            fontWeight: 500,
                                                            fontSize: '16px',
                                                            lineHeight: '1.52'
                                                        }, children: constants.application_modal_image_drag_drop }), _jsx(Typography, { variant: "caption", sx: {
                                                            color: '#949494',
                                                            fontSize: '12px',
                                                            lineHeight: '1.28'
                                                        }, children: constants.application_modal_image_browse })] })] }) }) }))] })] }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: handleClose, disabled: loading, sx: { color: '#26890d', textTransform: 'none' }, children: constants.cancel }), _jsx(Button, { onClick: handleSave, variant: "contained", disabled: loading, sx: {
                            backgroundColor: '#26890d',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#1f6b0a'
                            }
                        }, children: getSubmitButtonText() })] })] }));
};
export default ApplicationModal;
